import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDashboardMetrics } from '../hooks/useDashboardMetrics';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { Loader2, TrendingUp, Users, Target, MessageSquare } from 'lucide-react';
import { useAuth } from '@/shared/contexts/AuthContext';
import { Permission, hasPermission } from '@/shared/types/permissions';

/**
 * Dashboard de Feedback e PDI
 *
 * Esta é uma página de exemplo que mostra como consumir a API.
 * Você pode expandir com gráficos usando Recharts, tabelas, filtros, etc.
 */
export function FeedbackPDIDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Verificar se tem permissão de gestão
  useEffect(() => {
    if (user && !hasPermission(user.role, user.permissions, Permission.FEEDBACK_PDI_MANAGEMENT)) {
      navigate('/feedback-pdi/check-ins', { replace: true });
    }
  }, [user, navigate]);
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();

  const { data: metrics, isLoading, error } = useDashboardMetrics({
    month: currentMonth,
    year: currentYear,
  });

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600">Erro ao carregar métricas</h1>
          <p className="mt-2 text-muted-foreground">Tente novamente mais tarde</p>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return null;
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="mb-8">
        <h1 className="text-3xl font-bold">Dashboard de Feedback e PDI</h1>
        <p className="text-muted-foreground mt-2">
          Período: {currentMonth}/{currentYear}
        </p>
      </div>

      {/* Cards de Métricas Principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Taxa de Check-ins */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Check-ins Realizados</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {metrics.checkInCompletion.percentage.toFixed(0)}%
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {metrics.checkInCompletion.completed} de {metrics.checkInCompletion.total}
            </p>
          </CardContent>
        </Card>

        {/* Humor Médio */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Humor Médio</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {metrics.moodStats.average.toFixed(1)}/5
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {metrics.moodStats.lowMoodAlerts.length} alertas de humor baixo
            </p>
          </CardContent>
        </Card>

        {/* PDIs Ativos */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">PDIs Ativos</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.pdiStats.active}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {metrics.pdiStats.objectivesCompleted}/{metrics.pdiStats.totalObjectives} objetivos
              concluídos
            </p>
          </CardContent>
        </Card>

        {/* Micro-feedbacks */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Micro-feedbacks</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.feedbackStats.total}</div>
            <p className="text-xs text-muted-foreground mt-1">No período atual</p>
          </CardContent>
        </Card>
      </div>

      {/* Alertas */}
      {metrics.moodStats.lowMoodAlerts.length > 0 && (
        <Card className="mb-8 border-yellow-500">
          <CardHeader>
            <CardTitle>Alertas de Humor Baixo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {metrics.moodStats.lowMoodAlerts.map((alert) => (
                <div key={alert.checkInId} className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-950/40 rounded">
                  <div>
                    <p className="font-medium text-foreground">{alert.employeeName}</p>
                    <p className="text-sm text-muted-foreground">
                      Humor: {alert.moodScore}/5 - {new Date(alert.date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Check-ins Pendentes */}
      {metrics.checkInCompletion.pending.length > 0 && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Check-ins Pendentes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {metrics.checkInCompletion.pending.map((pending) => (
                <div key={pending.employeeId} className="flex items-center justify-between p-3 bg-muted rounded">
                  <p className="font-medium text-foreground">{pending.employeeName}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status dos Colaboradores */}
      <Card>
        <CardHeader>
          <CardTitle>Status dos Colaboradores</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Colaborador</th>
                  <th className="text-center p-2">Último Check-in</th>
                  <th className="text-center p-2">Humor</th>
                  <th className="text-center p-2">PDI Atual</th>
                  <th className="text-center p-2">Progresso</th>
                </tr>
              </thead>
              <tbody>
                {metrics.employeesStatus.map((employee) => (
                  <tr key={employee.employeeId} className="border-b">
                    <td className="p-2">{employee.employeeName}</td>
                    <td className="text-center p-2">
                      {employee.lastCheckIn ? (
                        new Date(employee.lastCheckIn.date).toLocaleDateString()
                      ) : (
                        <span className="text-yellow-600">Pendente</span>
                      )}
                    </td>
                    <td className="text-center p-2">
                      {employee.lastCheckIn ? `${employee.lastCheckIn.moodScore}/5` : '-'}
                    </td>
                    <td className="text-center p-2">
                      {employee.currentPDI ? `Q${employee.currentPDI.quarter}/${employee.currentPDI.year}` : '-'}
                    </td>
                    <td className="text-center p-2">
                      {employee.currentPDI
                        ? `${employee.currentPDI.objectivesCompleted}/${employee.currentPDI.totalObjectives}`
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* TODO: Adicionar gráficos de evolução usando Recharts */}
      {/* TODO: Adicionar filtros de período */}
      {/* TODO: Adicionar ações rápidas (criar check-in, criar PDI, etc) */}
    </div>
  );
}
